import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { authenticationState, customerState, type TCustomer } from '@repo/unicity'
import { countryState } from '@/core/store/country.store'
import { fetchProfilePictureFromMenu, uploadProfilePicture } from '../profile-picture.api'
import { buildProfilePicture, persistCustomerToStorage, type ProfilePicturePayload } from '../profile-picture.helpers'
import { useFilePicker } from './use-file-picker'

export interface UseProfilePictureUpload {
    inputRef: React.RefObject<HTMLInputElement | null>
    uploading: boolean
    error: string | null
    /** Source URL of the picked image, set while the crop tool is open. */
    pendingSrc: string | null
    /** Whether the crop modal should be visible. */
    cropOpen: boolean
    /** Trigger the hidden file input. */
    openFilePicker: () => void
    /** Wire to the hidden input's `onChange`. */
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    /** Called by the crop modal once the user confirms. */
    handleCropConfirm: (blob: Blob) => Promise<void>
    /** Called by the crop modal cancel button or backdrop. */
    closeCropTool: () => void
}

const hasSizes = (picture: ProfilePicturePayload | undefined): boolean => Boolean(picture?.sizes && picture.sizes.length > 0)

export const useProfilePictureUpload = (): UseProfilePictureUpload => {
    const filePicker = useFilePicker()
    const [cropOpen, setCropOpen] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const authentication = useAtomValue(authenticationState)
    const country = useAtomValue(countryState)
    const setCustomer = useSetAtom(customerState)

    const closeCropTool = () => {
        setCropOpen(false)
        filePicker.release()
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        filePicker.handleChange(event)
        if (event.target.files === null || event.target.files.length === 0) return
        setError(null)
        setCropOpen(true)
    }

    /**
     * Apply a profile-picture payload to `customerState` (cache-busting every URL)
     * and mirror the result to localStorage so a hard refresh keeps the new image.
     */
    const applyProfilePicture = (picture: ProfilePicturePayload | undefined) => {
        const stamp = Date.now()
        setCustomer(prev => {
            const next: TCustomer = {
                ...prev,
                profilePicture: buildProfilePicture(picture, prev.profilePicture, stamp)
            }
            persistCustomerToStorage(next)
            return next
        })
    }

    const handleCropConfirm = async (blob: Blob) => {
        // Close immediately — upload + menu refresh happen in the background.
        setCropOpen(false)
        filePicker.release()
        setError(null)
        setUploading(true)

        try {
            const uploadResp = await uploadProfilePicture({
                token: authentication.token,
                country2: country.shorter || '',
                file: blob,
                fileName: `${authentication.baId || 'profile'}.jpg`
            })

            // 1. Trust the upload response when it has new URLs (matches legacy behavior).
            if (hasSizes(uploadResp)) applyProfilePicture(uploadResp)

            // 2. Pull `onself.ushop.profile.profilePicture` from etlV2/menu so any
            //    other fields stay in sync. Apply whichever response is freshest.
            const menuPicture = await fetchProfilePictureFromMenu({
                token: authentication.token,
                countryAlpha3: country.short || '',
                baId: authentication.baId || ''
            })
            if (hasSizes(menuPicture)) {
                applyProfilePicture(menuPicture)
            } else if (!hasSizes(uploadResp)) {
                // Neither response had fresh sizes — at least cache-bust existing URLs.
                applyProfilePicture(undefined)
            }
        } catch (err) {
            console.error('uploadProfilePicture failed', err)
            setError('Update profile picture failed!')
        } finally {
            setUploading(false)
        }
    }

    return {
        inputRef: filePicker.inputRef,
        uploading,
        error,
        pendingSrc: filePicker.pickedSrc,
        cropOpen,
        openFilePicker: filePicker.open,
        handleFileChange,
        handleCropConfirm,
        closeCropTool
    }
}
